import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuth } from "../contexts/AuthContext";
import { apiClient } from "../services/apiClient";

// Components
import Layout from "../components/layout/Layout";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import CompanyContext from "../components/admin/CompanyContext";
import CompanyData from "../components/admin/CompanyData";
import ModelContent from "../components/admin/ModelContent";

const AdminPanel = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [systemStatus, setSystemStatus] = useState(null);
  const [users, setUsers] = useState([]);
  const [predefinedPrompts, setPredefinedPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Load initial data
  useEffect(() => {
    if (activeTab === "dashboard") {
      loadSystemStatus();
    } else if (activeTab === "users") {
      loadUsers();
    } else if (activeTab === "prompts") {
      loadPredefinedPrompts();
    }
  }, [activeTab]);

  const loadSystemStatus = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await apiClient.getSystemStatus();
      if (response.success) {
        setSystemStatus(response.data);
      } else {
        throw new Error(response.message || "Failed to load system status");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await apiClient.getUsers();
      if (response.success) {
        setUsers(response.data.users || []);
      } else {
        throw new Error(response.message || "Failed to load users");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPredefinedPrompts = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await apiClient.getPredefinedPrompts();
      if (response.success) {
        setPredefinedPrompts(response.data.prompts || []);
      } else {
        throw new Error(
          response.message || "Failed to load predefined prompts"
        );
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReprocessDocuments = async () => {
    if (
      !window.confirm(
        "This will reprocess all documents in the vector database. Continue?"
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.reprocessDocuments();
      if (response.success) {
        alert("Document reprocessing started successfully");
        loadSystemStatus(); // Refresh status
      } else {
        throw new Error(response.message || "Failed to start reprocessing");
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      healthy: "bg-emerald-100 text-emerald-800",
      degraded: "bg-amber-100 text-amber-800",
      unhealthy: "bg-red-100 text-red-800",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${
          statusColors[status] || statusColors.unhealthy
        }`}
      >
        {status}
      </span>
    );
  };

  // Tabs array
  const tabs = [
    { id: "dashboard", label: "System Overview", icon: "chart-bar" },
    { id: "users", label: "User Management", icon: "users" },
    { id: "company-context", label: "Company Context", icon: "building" },
    { id: "company-data", label: "Company Data", icon: "file-alt" },
    { id: "model-content", label: "Model Content", icon: "file-contract" },
    { id: "prompts", label: "Predefined Prompts", icon: "magic" },
    { id: "settings", label: "System Settings", icon: "cog" },
  ];

  return (
    <Layout title="Admin Panel">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center">
                <FontAwesomeIcon icon="shield" className="mr-3 text-red-500" />
                Admin Panel
              </h1>
              <p className="text-slate-600 mt-2">
                System administration and management tools
              </p>
            </div>
            <div className="text-sm text-slate-500">
              Logged in as:{" "}
              <span className="font-medium">{user?.username}</span>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="border-b border-slate-200">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? "border-red-500 text-red-600"
                      : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                  }`}
                >
                  <FontAwesomeIcon icon={tab.icon} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {error && (
              <ErrorMessage
                message={error}
                onRetry={() => setError("")}
                className="mb-6"
              />
            )}

            {/* System Overview Tab */}
            {activeTab === "dashboard" && (
              <DashboardTab
                loading={loading}
                systemStatus={systemStatus}
                formatDate={formatDate}
                getStatusBadge={getStatusBadge}
                handleReprocessDocuments={handleReprocessDocuments}
              />
            )}

            {/* User Management Tab */}
            {activeTab === "users" && (
              <UserManagement
                users={users}
                loading={loading}
                onRefresh={loadUsers}
              />
            )}

            {/* Company Context Tab */}
            {activeTab === "company-context" && <CompanyContext />}

            {/* Company Data Tab */}
            {activeTab === "company-data" && <CompanyData />}

            {/* Model Content Tab */}
            {activeTab === "model-content" && <ModelContent />}

            {/* Predefined Prompts Tab */}
            {activeTab === "prompts" && (
              <PredefinedPromptsManagement
                prompts={predefinedPrompts}
                loading={loading}
                onRefresh={loadPredefinedPrompts}
              />
            )}

            {/* System Settings Tab */}
            {activeTab === "settings" && <SystemSettings />}
          </div>
        </div>
      </div>
    </Layout>
  );
};

// Extract Dashboard content to keep main component clean
const DashboardTab = ({
  loading,
  systemStatus,
  formatDate,
  getStatusBadge,
  handleReprocessDocuments,
}) => {
  return (
    <div className="space-y-6">
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" text="Loading system status..." />
        </div>
      ) : systemStatus ? (
        <>
          {/* System Health Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-slate-900 mb-1">
                {systemStatus.database?.users || 0}
              </div>
              <div className="text-sm text-slate-600">Total Users</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-slate-900 mb-1">
                {systemStatus.database?.proposals || 0}
              </div>
              <div className="text-sm text-slate-600">Proposals</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-slate-900 mb-1">
                {systemStatus.database?.documents || 0}
              </div>
              <div className="text-sm text-slate-600">Documents</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-slate-900 mb-1">
                {systemStatus.database?.chat_sessions || 0}
              </div>
              <div className="text-sm text-slate-600">Chat Sessions</div>
            </div>
          </div>
          {/* Add your other dashboard sections here */}
        </>
      ) : (
        <div className="text-center py-12">
          <FontAwesomeIcon
            icon="exclamation-triangle"
            className="text-4xl text-slate-400 mb-4"
          />
          <p className="text-slate-600">No system status data available</p>
        </div>
      )}
    </div>
  );
};

// User Management Component
const UserManagement = ({ users, loading, onRefresh }) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // In the UserManagement component, update the handleCreateUser function:
  const handleCreateUser = async (userData) => {
    try {
      setActionLoading(true);

      // Extract selectedProposals from userData
      const { selectedProposals, ...userCreationData } = userData;

      // Create the user first
      const userResponse = await apiClient.createUser(userCreationData);
      if (!userResponse.success) {
        throw new Error(userResponse.message);
      }

      // Get the new user ID
      const newUserId = userResponse.data.userId;

      // Assign proposal access if proposals were selected
      if (selectedProposals && selectedProposals.length > 0) {
        const accessResponse = await apiClient.updateUserProposalAccess(
          newUserId,
          selectedProposals
        );
        if (!accessResponse.success) {
          console.warn(
            "User created but proposal access assignment failed:",
            accessResponse.message
          );
          // Don't throw error here since user was created successfully
        }
      }

      onRefresh();
      setShowCreateForm(false);
      alert(
        `User created successfully! ${
          selectedProposals?.length > 0
            ? `Access granted to ${selectedProposals.length} proposals.`
            : ""
        }`
      );
    } catch (error) {
      alert(`Error creating user: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // In AdminPanel.js, update the handleUpdateUser function in the UserManagement component:

  const handleUpdateUser = async (userId, updates) => {
    try {
      setActionLoading(true);

      // Extract password and selectedProposals from updates
      const { selectedProposals, newPassword, ...userUpdates } = updates;

      // Update user information
      const userResponse = await apiClient.updateUser(userId, userUpdates);
      if (!userResponse.success) {
        throw new Error(userResponse.message);
      }

      // Reset password if provided
      if (newPassword) {
        const passwordResponse = await apiClient.resetUserPassword(userId, newPassword);
        if (!passwordResponse.success) {
          throw new Error(passwordResponse.message);
        }
      }

      // Update proposal access if provided
      if (selectedProposals !== undefined) {
        const accessResponse = await apiClient.updateUserProposalAccess(
          userId,
          selectedProposals
        );
        if (!accessResponse.success) {
          throw new Error(accessResponse.message);
        }
      }

      onRefresh();
      setSelectedUser(null);
      alert("User updated successfully!" + (newPassword ? " Password has been reset." : ""));
    } catch (error) {
      alert(`Error updating user: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (
      !window.confirm(`Are you sure you want to deactivate user: ${username}?`)
    ) {
      return;
    }

    try {
      setActionLoading(true);
      const response = await apiClient.deleteUser(userId);
      if (response.success) {
        onRefresh();
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      alert(`Error deactivating user: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" text="Loading users..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">
          User Management ({users.length} users)
        </h3>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200"
          disabled={actionLoading}
        >
          <FontAwesomeIcon icon="plus" className="mr-2" />
          Create User
        </button>
      </div>

      {/* Users Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Activity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {users.map((user) => (
                <tr key={user.user_id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-slate-900">
                        {user.username}
                      </div>
                      <div className="text-sm text-slate-500">{user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.role === "admin"
                          ? "bg-red-100 text-red-800"
                          : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    <div>
                      <div>
                        Created:{" "}
                        {new Date(user.created_at).toLocaleDateString()}
                      </div>
                      <div>
                        Last login:{" "}
                        {user.last_login
                          ? new Date(user.last_login).toLocaleDateString()
                          : "Never"}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.is_active
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-slate-100 text-slate-800"
                      }`}
                    >
                      {user.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => setSelectedUser(user)}
                      className="text-red-600 hover:text-red-900"
                      disabled={actionLoading}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() =>
                        handleDeleteUser(user.user_id, user.username)
                      }
                      className="text-red-600 hover:text-red-900"
                      disabled={actionLoading || !user.is_active}
                    >
                      Deactivate
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateForm && (
        <CreateUserModal
          onClose={() => setShowCreateForm(false)}
          onSubmit={handleCreateUser}
          loading={actionLoading}
        />
      )}

      {/* Edit User Modal */}
      {selectedUser && (
        <EditUserModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onSubmit={(updates) =>
            handleUpdateUser(selectedUser.user_id, updates)
          }
          loading={actionLoading}
        />
      )}
    </div>
  );
};

// Predefined Prompts Management Component
const PredefinedPromptsManagement = ({ prompts, loading, onRefresh }) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const handleCreatePrompt = async (promptData) => {
    try {
      setActionLoading(true);
      const response = await apiClient.createPredefinedPrompt(promptData);
      if (response.success) {
        onRefresh();
        setShowCreateForm(false);
        alert("Prompt created successfully!");
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      alert(`Error creating prompt: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdatePrompt = async (promptId, updates) => {
    try {
      setActionLoading(true);
      const response = await apiClient.updatePredefinedPrompt(
        promptId,
        updates
      );
      if (response.success) {
        onRefresh();
        setSelectedPrompt(null);
        alert("Prompt updated successfully!");
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      alert(`Error updating prompt: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeletePrompt = async (promptId, promptName) => {
    if (
      !window.confirm(`Are you sure you want to delete prompt: ${promptName}?`)
    ) {
      return;
    }

    try {
      setActionLoading(true);
      const response = await apiClient.deletePredefinedPrompt(promptId);
      if (response.success) {
        onRefresh();
        alert("Prompt deleted successfully!");
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      alert(`Error deleting prompt: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const filteredPrompts = prompts.filter((prompt) => {
    const matchesFilter =
      !filter ||
      prompt.prompt_name?.toLowerCase().includes(filter.toLowerCase()) ||
      prompt.prompt_text?.toLowerCase().includes(filter.toLowerCase()) ||
      prompt.description?.toLowerCase().includes(filter.toLowerCase());

    const matchesCategory =
      !categoryFilter || prompt.category === categoryFilter;

    return matchesFilter && matchesCategory;
  });

  const uniqueCategories = [
    ...new Set(prompts.map((prompt) => prompt.category)),
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" text="Loading prompts..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">
          Predefined Prompts ({filteredPrompts.length} prompts)
        </h3>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200"
          disabled={actionLoading}
        >
          <FontAwesomeIcon icon="plus" className="mr-2" />
          Create Prompt
        </button>
      </div>

      {/* Filters */}
      <div className="flex space-x-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search prompts..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
          />
        </div>
        <div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
          >
            <option value="">All Categories</option>
            {uniqueCategories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Prompts Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {filteredPrompts.map((prompt) => (
                <tr key={prompt.prompt_id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-slate-900">
                        {prompt.prompt_name}
                      </div>
                      <div className="text-sm text-slate-500 truncate max-w-xs">
                        {prompt.prompt_text.substring(0, 100)}...
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                      {prompt.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 max-w-xs truncate">
                    {prompt.description || "No description"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        prompt.is_active
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-slate-100 text-slate-800"
                      }`}
                    >
                      {prompt.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => setSelectedPrompt(prompt)}
                      className="text-red-600 hover:text-red-900"
                      disabled={actionLoading}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() =>
                        handleDeletePrompt(prompt.prompt_id, prompt.prompt_name)
                      }
                      className="text-red-600 hover:text-red-900"
                      disabled={actionLoading}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Empty State */}
      {filteredPrompts.length === 0 && !loading && (
        <div className="text-center py-12">
          <FontAwesomeIcon
            icon="magic"
            className="text-4xl text-slate-300 mb-4"
          />
          <h3 className="text-lg font-medium text-slate-900 mb-2">
            No prompts found
          </h3>
          <p className="text-slate-600 mb-4">
            {filter || categoryFilter
              ? "Try adjusting your search filters"
              : "Get started by creating your first predefined prompt"}
          </p>
          {!filter && !categoryFilter && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors duration-200"
            >
              <FontAwesomeIcon icon="plus" className="mr-2" />
              Create First Prompt
            </button>
          )}
        </div>
      )}

      {/* Create Prompt Modal */}
      {showCreateForm && (
        <CreatePromptModal
          onClose={() => setShowCreateForm(false)}
          onSubmit={handleCreatePrompt}
          loading={actionLoading}
          categories={uniqueCategories}
        />
      )}

      {/* Edit Prompt Modal */}
      {selectedPrompt && (
        <EditPromptModal
          prompt={selectedPrompt}
          onClose={() => setSelectedPrompt(null)}
          onSubmit={(updates) =>
            handleUpdatePrompt(selectedPrompt.prompt_id, updates)
          }
          loading={actionLoading}
          categories={uniqueCategories}
        />
      )}
    </div>
  );
};

// System Settings Component
const SystemSettings = () => {
  const [allAvailableModels, setAllAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAllModels();
  }, []);

  const loadAllModels = async () => {
    try {
      const response = await apiClient.getAIModels();
      if (response.success && response.data.models) {
        setAllAvailableModels(response.data.models);

        // Find and set the current default model
        const defaultModel = response.data.models.find(m => m.isDefault);
        if (defaultModel) {
          setSelectedModel(defaultModel.model);
        }
      }
    } catch (error) {
      console.error("Failed to load available models:", error);
    }
  };

  const handleModelChange = async (modelName) => {
    try {
      setLoading(true);

      const response = await apiClient.setDefaultAIModel(modelName);

      if (response.success) {
        setSelectedModel(modelName);
        alert(`Default AI model set to ${modelName}`);
        // Reload to update the default marker
        await loadAllModels();
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error("Failed to set default model:", error);
      alert(`Error setting default model: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-slate-900">System Settings</h3>

      {/* AI Model Configuration */}
      <div className="bg-slate-50 rounded-lg p-6">
        <h4 className="text-md font-semibold text-slate-900 mb-4">
          Default AI Model
        </h4>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Select Default AI Model
            </label>
            <p className="text-xs text-slate-600 mb-3">
              This model will be used for all AI operations unless specifically overridden (e.g., in holistic proposal generation).
            </p>

            <select
              value={selectedModel}
              onChange={(e) => handleModelChange(e.target.value)}
              disabled={loading}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-50 disabled:cursor-not-allowed text-base"
            >
              <option value="">Select AI Model...</option>

              {/* OpenAI Models */}
              {allAvailableModels.filter(m => m.provider === 'openai').length > 0 && (
                <optgroup label="OpenAI">
                  {allAvailableModels
                    .filter(m => m.provider === 'openai')
                    .map((model) => (
                      <option key={model.model} value={model.model}>
                        {model.name} {model.isDefault && '(Current Default)'}
                      </option>
                    ))}
                </optgroup>
              )}

              {/* Anthropic Models */}
              {allAvailableModels.filter(m => m.provider === 'anthropic').length > 0 && (
                <optgroup label="Anthropic">
                  {allAvailableModels
                    .filter(m => m.provider === 'anthropic')
                    .map((model) => (
                      <option key={model.model} value={model.model}>
                        {model.name} {model.isDefault && '(Current Default)'}
                      </option>
                    ))}
                </optgroup>
              )}

              {/* Google Models */}
              {allAvailableModels.filter(m => m.provider === 'google').length > 0 && (
                <optgroup label="Google">
                  {allAvailableModels
                    .filter(m => m.provider === 'google')
                    .map((model) => (
                      <option key={model.model} value={model.model}>
                        {model.name} {model.isDefault && '(Current Default)'}
                      </option>
                    ))}
                </optgroup>
              )}
            </select>

            {selectedModel && (
              <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-xs text-green-800">
                  <strong>Current Default:</strong> {allAvailableModels.find(m => m.model === selectedModel)?.name || selectedModel}
                </p>
              </div>
            )}
          </div>

          <div className="pt-3 border-t border-slate-200">
            <p className="text-xs text-slate-600">
              <strong>Note:</strong> The default model is used system-wide for chat, document generation, and other AI features.
              Users can override this selection when creating holistic proposals.
            </p>
          </div>
        </div>
      </div>

      {/* Maintenance Actions */}
      <div className="bg-slate-50 rounded-lg p-6">
        <h4 className="text-md font-semibold text-slate-900 mb-4">
          Maintenance Actions
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => {
              if (
                window.confirm("This will reprocess all documents. Continue?")
              ) {
                // Handled by parent component
              }
            }}
            className="p-4 bg-white border border-slate-300 rounded-lg hover:border-red-300 hover:bg-red-50 transition-colors duration-200 text-left"
          >
            <div className="flex items-center space-x-3">
              <FontAwesomeIcon icon="refresh" className="text-red-500" />
              <div>
                <div className="font-medium text-slate-900">
                  Reprocess Documents
                </div>
                <div className="text-sm text-slate-600">
                  Regenerate vector embeddings
                </div>
              </div>
            </div>
          </button>

          <button
            onClick={() => alert("Cache clearing functionality coming soon")}
            className="p-4 bg-white border border-slate-300 rounded-lg hover:border-red-300 hover:bg-red-50 transition-colors duration-200 text-left"
          >
            <div className="flex items-center space-x-3">
              <FontAwesomeIcon icon="trash" className="text-red-500" />
              <div>
                <div className="font-medium text-slate-900">Clear Cache</div>
                <div className="text-sm text-slate-600">
                  Reset application cache
                </div>
              </div>
            </div>
          </button>

          <button
            onClick={() => window.location.reload()}
            className="p-4 bg-white border border-slate-300 rounded-lg hover:border-red-300 hover:bg-red-50 transition-colors duration-200 text-left"
          >
            <div className="flex items-center space-x-3">
              <FontAwesomeIcon icon="sync-alt" className="text-red-500" />
              <div>
                <div className="font-medium text-slate-900">
                  Restart Interface
                </div>
                <div className="text-sm text-slate-600">
                  Reload the admin interface
                </div>
              </div>
            </div>
          </button>

          <button
            onClick={() => alert("Backup functionality coming soon")}
            className="p-4 bg-white border border-slate-300 rounded-lg hover:border-red-300 hover:bg-red-50 transition-colors duration-200 text-left"
          >
            <div className="flex items-center space-x-3">
              <FontAwesomeIcon icon="download" className="text-red-500" />
              <div>
                <div className="font-medium text-slate-900">Create Backup</div>
                <div className="text-sm text-slate-600">Export system data</div>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* System Information */}
      <div className="bg-slate-50 rounded-lg p-6">
        <h4 className="text-md font-semibold text-slate-900 mb-4">
          System Information
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h5 className="text-sm font-medium text-slate-700 mb-2">
              Application
            </h5>
            <div className="space-y-1 text-sm text-slate-600">
              <div>Version: 1.0.0</div>
              <div>Environment: {process.env.NODE_ENV || "development"}</div>
              <div>Build: {new Date().toLocaleDateString()}</div>
            </div>
          </div>

          <div>
            <h5 className="text-sm font-medium text-slate-700 mb-2">
              Configuration
            </h5>
            <div className="space-y-1 text-sm text-slate-600">
              <div>Max File Size: 50MB</div>
              <div>Session Timeout: 24 hours</div>
              <div>Vector DB: ChromaDB</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Updated CreateUserModal Component with Proposal Assignment
const CreateUserModal = ({ onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    role: "user",
  });

  const [proposals, setProposals] = useState([]);
  const [selectedProposals, setSelectedProposals] = useState([]);
  const [loadingProposals, setLoadingProposals] = useState(false);
  const [errors, setErrors] = useState({});
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
  });

  // Load proposals when modal opens
  useEffect(() => {
    loadProposals();
  }, []);

  const loadProposals = async () => {
    try {
      setLoadingProposals(true);
      const response = await apiClient.getProposals({ limit: 100 });
      if (response.success) {
        setProposals(response.data.proposals);
      }
    } catch (err) {
      console.error("Failed to load proposals:", err);
    } finally {
      setLoadingProposals(false);
    }
  };

  // Password validation function
  const validatePassword = (password) => {
    const validation = {
      length: password.length >= 6,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
    };
    setPasswordValidation(validation);
    return Object.values(validation).every(Boolean);
  };

  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setFormData({ ...formData, password });
    validatePassword(password);
  };

  const handleProposalToggle = (proposalId) => {
    setSelectedProposals((prev) => {
      if (prev.includes(proposalId)) {
        return prev.filter((id) => id !== proposalId);
      } else {
        return [...prev, proposalId];
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Basic validation
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (!validatePassword(formData.password)) {
      newErrors.password = "Password does not meet requirements";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Include selected proposals in submission
    // Backend will auto-generate username from email
    onSubmit({
      email: formData.email,
      password: formData.password,
      role: formData.role,
      selectedProposals: selectedProposals,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">
            Create New User
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <FontAwesomeIcon icon="times" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - User Details */}
            <div className="space-y-4">
              <h4 className="text-md font-semibold text-slate-900 border-b border-slate-200 pb-2">
                User Information
              </h4>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    errors.email ? "border-red-300" : "border-slate-300"
                  }`}
                  placeholder="user@example.com"
                  required
                />
                {errors.email && (
                  <p className="text-red-600 text-sm mt-1">{errors.email}</p>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  This will be used for login
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Role
                </label>
                <select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={handlePasswordChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    errors.password ? "border-red-300" : "border-slate-300"
                  }`}
                  placeholder="Enter secure password"
                />

                {/* Password Requirements */}
                <div className="mt-2 space-y-1">
                  <div className="text-xs text-slate-600 mb-1">
                    Password must contain:
                  </div>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <div
                      className={`flex items-center ${
                        passwordValidation.length
                          ? "text-green-600"
                          : "text-slate-400"
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={passwordValidation.length ? "check" : "times"}
                        className="mr-1 text-xs"
                      />
                      At least 6 characters
                    </div>
                    <div
                      className={`flex items-center ${
                        passwordValidation.lowercase
                          ? "text-green-600"
                          : "text-slate-400"
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={passwordValidation.lowercase ? "check" : "times"}
                        className="mr-1 text-xs"
                      />
                      Lowercase letter
                    </div>
                    <div
                      className={`flex items-center ${
                        passwordValidation.uppercase
                          ? "text-green-600"
                          : "text-slate-400"
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={passwordValidation.uppercase ? "check" : "times"}
                        className="mr-1 text-xs"
                      />
                      Uppercase letter
                    </div>
                    <div
                      className={`flex items-center ${
                        passwordValidation.number
                          ? "text-green-600"
                          : "text-slate-400"
                      }`}
                    >
                      <FontAwesomeIcon
                        icon={passwordValidation.number ? "check" : "times"}
                        className="mr-1 text-xs"
                      />
                      Number
                    </div>
                  </div>
                </div>

                {errors.password && (
                  <p className="text-red-600 text-sm mt-1">{errors.password}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      confirmPassword: e.target.value,
                    })
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    errors.confirmPassword
                      ? "border-red-300"
                      : "border-slate-300"
                  }`}
                  placeholder="Confirm your password"
                />
                {errors.confirmPassword && (
                  <p className="text-red-600 text-sm mt-1">
                    {errors.confirmPassword}
                  </p>
                )}
              </div>
            </div>

            {/* Right Column - Proposal Access */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                <h4 className="text-md font-semibold text-slate-900">
                  Proposal Access
                </h4>
                <div className="text-sm text-slate-500">
                  {selectedProposals.length} proposal
                  {selectedProposals.length !== 1 ? "s" : ""} selected
                </div>
              </div>

              {loadingProposals ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="sm" text="Loading proposals..." />
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-lg">
                    {proposals.length === 0 ? (
                      <div className="p-4 text-center text-slate-500">
                        No proposals found
                      </div>
                    ) : (
                      <div className="p-3 space-y-2">
                        {proposals.map((proposal) => (
                          <label
                            key={proposal.proposal_id}
                            className="flex items-center p-2 rounded hover:bg-slate-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedProposals.includes(
                                proposal.proposal_id
                              )}
                              onChange={() =>
                                handleProposalToggle(proposal.proposal_id)
                              }
                              className="mr-3 h-4 w-4 text-red-600 focus:ring-red-500 border-slate-300 rounded"
                            />
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="text-sm font-medium text-slate-900">
                                    {proposal.proposal_name}
                                  </div>
                                  <div className="text-xs text-slate-500">
                                    {proposal.customer_name} • {proposal.status}
                                  </div>
                                </div>
                                <span
                                  className={`px-2 py-1 text-xs rounded-full ${
                                    proposal.proposal_type === "RFP"
                                      ? "bg-orange-100 text-orange-700"
                                      : "bg-purple-100 text-purple-700"
                                  }`}
                                >
                                  {proposal.proposal_type}
                                </span>
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="text-sm text-blue-700">
                      <p className="font-medium mb-1">Access Management</p>
                      <p>
                        Select which proposals this user can access. You can
                        modify access later if needed.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-700 hover:text-slate-900"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                loading || !Object.values(passwordValidation).every(Boolean)
              }
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Creating..." : "Create User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit User Modal Component
// Add these new components to AdminPanel.js:

// Update the EditUserModal component to include proposal management
const EditUserModal = ({ user, onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    username: user.username || "",
    email: user.email || "",
    role: user.role || "user",
    is_active: user.is_active !== undefined ? user.is_active : true,
  });

  const [proposals, setProposals] = useState([]);
  const [selectedProposals, setSelectedProposals] = useState([]);
  const [loadingProposals, setLoadingProposals] = useState(false);

  // Password reset state
  const [changePassword, setChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
  });

  useEffect(() => {
    loadProposalsAndAccess();
  }, [user.user_id]);

  const loadProposalsAndAccess = async () => {
    try {
      setLoadingProposals(true);

      // Load all proposals
      const proposalsResponse = await apiClient.getProposals({ limit: 100 });
      if (proposalsResponse.success) {
        setProposals(proposalsResponse.data.proposals);
      }

      // Load user's current proposal access
      const accessResponse = await apiClient.getUserProposalAccess(
        user.user_id
      );
      if (accessResponse.success) {
        const accessibleProposalIds = accessResponse.data.proposals.map(
          (p) => p.proposal_id
        );
        setSelectedProposals(accessibleProposalIds);
      }
    } catch (err) {
      console.error("Failed to load proposals:", err);
    } finally {
      setLoadingProposals(false);
    }
  };

  const handleProposalToggle = (proposalId) => {
    setSelectedProposals((prev) => {
      if (prev.includes(proposalId)) {
        return prev.filter((id) => id !== proposalId);
      } else {
        return [...prev, proposalId];
      }
    });
  };

  // Password validation function
  const validatePassword = (password) => {
    const validation = {
      length: password.length >= 6,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
    };
    setPasswordValidation(validation);
    return Object.values(validation).every(Boolean);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      selectedProposals: selectedProposals,
    };

    // Add password if changing
    if (changePassword && newPassword) {
      if (newPassword !== confirmPassword) {
        alert("Passwords do not match");
        return;
      }
      if (!Object.values(passwordValidation).every(Boolean)) {
        alert("Password does not meet requirements");
        return;
      }
      submitData.newPassword = newPassword;
    }

    onSubmit(submitData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Edit User</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <FontAwesomeIcon icon="times" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - User Details */}
            <div className="space-y-4">
              <h4 className="text-md font-semibold text-slate-900 border-b border-slate-200 pb-2">
                User Information
              </h4>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) =>
                    setFormData({ ...formData, username: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Role
                </label>
                <select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-slate-700">
                    Active User
                  </span>
                </label>
              </div>

              {/* Password Reset Section */}
              <div className="border-t border-slate-200 pt-4 mt-4">
                <label className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    checked={changePassword}
                    onChange={(e) => {
                      setChangePassword(e.target.checked);
                      if (!e.target.checked) {
                        setNewPassword("");
                        setConfirmPassword("");
                      }
                    }}
                    className="mr-2 h-4 w-4 text-red-600 focus:ring-red-500 border-slate-300 rounded"
                  />
                  <span className="text-sm font-medium text-slate-700">
                    Change Password
                  </span>
                </label>

                {changePassword && (
                  <div className="space-y-4 pl-6 border-l-2 border-blue-200">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        New Password
                      </label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => {
                          setNewPassword(e.target.value);
                          validatePassword(e.target.value);
                        }}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="Enter new password"
                      />

                      {/* Password Requirements Indicator */}
                      <div className="mt-2 space-y-1">
                        <div className="text-xs text-slate-600 mb-1">
                          Password must contain:
                        </div>
                        <div className="grid grid-cols-2 gap-1 text-xs">
                          <div className={`flex items-center ${
                            passwordValidation.length ? "text-green-600" : "text-slate-400"
                          }`}>
                            <FontAwesomeIcon
                              icon={passwordValidation.length ? "check" : "times"}
                              className="mr-1 text-xs"
                            />
                            At least 6 characters
                          </div>
                          <div className={`flex items-center ${
                            passwordValidation.lowercase ? "text-green-600" : "text-slate-400"
                          }`}>
                            <FontAwesomeIcon
                              icon={passwordValidation.lowercase ? "check" : "times"}
                              className="mr-1 text-xs"
                            />
                            Lowercase letter
                          </div>
                          <div className={`flex items-center ${
                            passwordValidation.uppercase ? "text-green-600" : "text-slate-400"
                          }`}>
                            <FontAwesomeIcon
                              icon={passwordValidation.uppercase ? "check" : "times"}
                              className="mr-1 text-xs"
                            />
                            Uppercase letter
                          </div>
                          <div className={`flex items-center ${
                            passwordValidation.number ? "text-green-600" : "text-slate-400"
                          }`}>
                            <FontAwesomeIcon
                              icon={passwordValidation.number ? "check" : "times"}
                              className="mr-1 text-xs"
                            />
                            Number
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Confirm New Password
                      </label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                          confirmPassword && newPassword !== confirmPassword
                            ? "border-red-300"
                            : "border-slate-300"
                        }`}
                        placeholder="Confirm new password"
                      />
                      {confirmPassword && newPassword !== confirmPassword && (
                        <p className="text-red-600 text-sm mt-1">Passwords do not match</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Proposal Access */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                <h4 className="text-md font-semibold text-slate-900">
                  Proposal Access
                </h4>
                <div className="text-sm text-slate-500">
                  {selectedProposals.length} proposal
                  {selectedProposals.length !== 1 ? "s" : ""} selected
                </div>
              </div>

              {loadingProposals ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="sm" text="Loading proposals..." />
                </div>
              ) : (
                <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-lg">
                  {proposals.length === 0 ? (
                    <div className="p-4 text-center text-slate-500">
                      No proposals found
                    </div>
                  ) : (
                    <div className="p-3 space-y-2">
                      {proposals.map((proposal) => (
                        <label
                          key={proposal.proposal_id}
                          className="flex items-center p-2 rounded hover:bg-slate-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedProposals.includes(
                              proposal.proposal_id
                            )}
                            onChange={() =>
                              handleProposalToggle(proposal.proposal_id)
                            }
                            className="mr-3 h-4 w-4 text-red-600 focus:ring-red-500 border-slate-300 rounded"
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-sm font-medium text-slate-900">
                                  {proposal.proposal_name}
                                </div>
                                <div className="text-xs text-slate-500">
                                  {proposal.customer_name} • {proposal.status}
                                </div>
                              </div>
                              <span
                                className={`px-2 py-1 text-xs rounded-full ${
                                  proposal.proposal_type === "RFP"
                                    ? "bg-orange-100 text-orange-700"
                                    : "bg-purple-100 text-purple-700"
                                }`}
                              >
                                {proposal.proposal_type}
                              </span>
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="text-sm text-blue-700">
                  <p className="font-medium mb-1">Access Management</p>
                  <p>
                    Select which proposals this user can access. Changes take
                    effect immediately.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-700 hover:text-slate-900"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
            >
              {loading ? "Updating..." : "Update User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Create Prompt Modal Component
const CreatePromptModal = ({ onClose, onSubmit, loading, categories }) => {
  const [formData, setFormData] = useState({
    prompt_name: "",
    prompt_text: "",
    category: "General",
    description: "",
    sort_order: 0,
    is_active: true,
  });
  const [errors, setErrors] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();

    // Basic validation
    const newErrors = {};
    if (!formData.prompt_name) newErrors.prompt_name = "Name is required";
    if (!formData.prompt_text)
      newErrors.prompt_text = "Prompt text is required";
    if (!formData.category) newErrors.category = "Category is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">
            Create New Prompt
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <FontAwesomeIcon icon="times" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Prompt Name
            </label>
            <input
              type="text"
              value={formData.prompt_name}
              onChange={(e) =>
                setFormData({ ...formData, prompt_name: e.target.value })
              }
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                errors.prompt_name ? "border-red-300" : "border-slate-300"
              }`}
              placeholder="e.g. RFP Compliance Check"
            />
            {errors.prompt_name && (
              <p className="text-red-600 text-sm mt-1">{errors.prompt_name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) =>
                setFormData({ ...formData, category: e.target.value })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="General">General</option>
              <option value="Analysis">Analysis</option>
              <option value="Review">Review</option>
              <option value="Strategy">Strategy</option>
              <option value="Writing">Writing</option>
              <option value="Compliance">Compliance</option>
              <option value="Risk Management">Risk Management</option>
              <option value="Pricing">Pricing</option>
              <option value="Section Development">Section Development</option>
              <option value="Final Review">Final Review</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Description
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="Brief description of the prompt's purpose"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Prompt Text
            </label>
            <textarea
              value={formData.prompt_text}
              onChange={(e) =>
                setFormData({ ...formData, prompt_text: e.target.value })
              }
              rows={6}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                errors.prompt_text ? "border-red-300" : "border-slate-300"
              }`}
              placeholder="Enter the prompt text that users will see when they select this prompt..."
            />
            {errors.prompt_text && (
              <p className="text-red-600 text-sm mt-1">{errors.prompt_text}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Sort Order
              </label>
              <input
                type="number"
                value={formData.sort_order}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    sort_order: parseInt(e.target.value) || 0,
                  })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                min="0"
              />
            </div>

            <div className="flex items-center mt-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                  className="mr-2"
                />
                <span className="text-sm font-medium text-slate-700">
                  Active
                </span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-700 hover:text-slate-900"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
            >
              {loading ? "Creating..." : "Create Prompt"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Prompt Modal Component
const EditPromptModal = ({
  prompt,
  onClose,
  onSubmit,
  loading,
  categories,
}) => {
  const [formData, setFormData] = useState({
    prompt_name: prompt.prompt_name || "",
    prompt_text: prompt.prompt_text || "",
    category: prompt.category || "General",
    description: prompt.description || "",
    sort_order: prompt.sort_order || 0,
    is_active: prompt.is_active !== undefined ? prompt.is_active : true,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Edit Prompt</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <FontAwesomeIcon icon="times" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Prompt Name
            </label>
            <input
              type="text"
              value={formData.prompt_name}
              onChange={(e) =>
                setFormData({ ...formData, prompt_name: e.target.value })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) =>
                setFormData({ ...formData, category: e.target.value })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="General">General</option>
              <option value="Analysis">Analysis</option>
              <option value="Review">Review</option>
              <option value="Strategy">Strategy</option>
              <option value="Writing">Writing</option>
              <option value="Compliance">Compliance</option>
              <option value="Risk Management">Risk Management</option>
              <option value="Pricing">Pricing</option>
              <option value="Section Development">Section Development</option>
              <option value="Final Review">Final Review</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Description
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="Brief description of the prompt's purpose"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Prompt Text
            </label>
            <textarea
              value={formData.prompt_text}
              onChange={(e) =>
                setFormData({ ...formData, prompt_text: e.target.value })
              }
              rows={6}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="Enter the prompt text..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Sort Order
              </label>
              <input
                type="number"
                value={formData.sort_order}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    sort_order: parseInt(e.target.value) || 0,
                  })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                min="0"
              />
            </div>

            <div className="flex items-center mt-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                  className="mr-2"
                />
                <span className="text-sm font-medium text-slate-700">
                  Active
                </span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-700 hover:text-slate-900"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
            >
              {loading ? "Updating..." : "Update Prompt"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminPanel;
